import { BarChart, BarChartOption, ChartHandle, Loader, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowResizeListener } from '../../../../hooks/common';
import useRefEventListener from '../../../../hooks/common/useRefEventListener';
import useFetchChangeChart from '../../../../hooks/riskProducts/dashboard/useFetchChangeChart';
import useFetchWidgetMetrics from '../../../../hooks/riskProducts/dashboard/useFetchWidgetMetrics';
import { ChangeChartMetric } from '../../../../services/dtos/riskProducts/changeCharts.dto';
import { RiskDistributionGroupBy } from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import { downloadPng } from '../../../../utils/chartUtils';
import { sleep } from '../../../../utils/helperUtils';
import { MODAL_TYPES, useGlobalModalContext } from '../../../GlobalModal';
import NoResults from '../../../NoResults';
import ChartActions from '../ChartActions';
import { getChartConfig, getWidgetName, isPointInGrid } from './ChangeChart.logic';
import styles from './ChangeChart.module.scss';

const cx = classNames.bind(styles);

interface ChangeChartProps {
  watchListId: string;
}

const ChangeChart = ({ watchListId }: ChangeChartProps): ReactElement => {
  const { showModal } = useGlobalModalContext();
  const widgetRef = useRef<HTMLElement>(null);
  const chartHandle = useRef<ChartHandle>(null);
  const [widgetWidth, setWidgetWidth] = useState<number | undefined>();

  const [selectedGroupBy, setSelectedGroupBy] = useState<RiskDistributionGroupBy>(RiskDistributionGroupBy.SECTOR);
  const [selectedMetric, setSelectedMetric] = useState<ChangeChartMetric | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [unratedToggleOn, setUnratedToggleOn] = useState(false);

  const { data, isLoading, isError, refetch } = useFetchChangeChart(watchListId, selectedGroupBy, !unratedToggleOn);
  const { data: widgetMetrics } = useFetchWidgetMetrics();
  const widgetId = WIDGET_ID.CHANGE_CHART;

  const selectedMetricTitle = useMemo(
    () => getWidgetName(selectedMetric, widgetMetrics?.CHANGE_CHART),
    [widgetMetrics, selectedMetric]
  );

  useEffect(() => {
    setWidgetWidth(widgetRef.current?.clientWidth);
    return () => {
      chartHandle.current?.chartInstance?.dispose();
    };
  }, []);

  useWindowResizeListener(() => {
    chartHandle.current?.chartInstance?.resize();
    setWidgetWidth(widgetRef.current?.clientWidth);
  });

  const chartOptions: BarChartOption | undefined = useMemo(() => {
    return getChartConfig(data, selectedMetric, widgetWidth ?? 0);
  }, [data, selectedMetric, widgetWidth]);

  const onDownload = useCallback(async () => {
    if (chartHandle.current && chartHandle.current.chartInstance) {
      setIsDownloading(true);

      chartHandle.current.chartInstance.setOption(
        getChartConfig(data, selectedMetric, widgetWidth ?? 0, widgetMetrics?.CHANGE_CHART, true)!,
        true
      );
      downloadPng(chartHandle.current.chartInstance, `average-${selectedMetric}`);
      chartHandle.current.chartInstance.setOption(chartOptions!, true);

      await sleep(500);

      setIsDownloading(false);
    }
  }, [selectedMetric, selectedGroupBy, chartOptions]);

  const getSubHeader = (model, value): string => {
    if (selectedMetric) {
      return `${model}: ${selectedMetric.startsWith('cqs') ? 'CQS ' : 'CRE '} ${round(value, 2)}`;
    }
    return '';
  };

  const showChangeChartsModal = useRefEventListener((model: string, value: number, dataIndex: number) => {
    if (data) {
      showModal(MODAL_TYPES.CHART_DRILL_DOWN, {
        title: selectedMetricTitle,
        subHeader: getSubHeader(model, value),
        companies: data[dataIndex].companyIds,
        groupBy: selectedGroupBy,
        widget: widgetId,
        metric: selectedMetric,
      });
    }
  });

  const onChartInitialized = () => {
    const chartInstance = chartHandle.current?.chartInstance;
    const chartInstanceZr = chartHandle.current?.chartInstance?.getZr();

    const getBarIndex = (event) => {
      const pointInPixel = [event.offsetX, event.offsetY];
      return chartInstance!.convertFromPixel('grid', pointInPixel)[0];
    };

    if (!(chartInstance && chartInstanceZr)) {
      return;
    }

    chartInstanceZr.on('mousemove', (event) => {
      // @ts-ignore
      if (isPointInGrid(getBarIndex(event), event.offsetY, [0, chartInstance.getOption().series.length - 1])) {
        chartInstanceZr.setCursorStyle('pointer');
      }
    });

    chartInstanceZr.on('click', (event) => {
      const barIndex = getBarIndex(event);
      // @ts-ignore
      if (isPointInGrid(getBarIndex(event), event.offsetY, [0, chartInstance.getOption().series.length - 1])) {
        showChangeChartsModal.current(
          // @ts-ignore
          chartInstance.getOption().xAxis[0].data[barIndex],
          // @ts-ignore
          chartInstance.getOption().series[barIndex].data[barIndex],
          barIndex
        );
      }
    });
  };

  const renderFirstLine = () => {
    return (
      <ChartActions
        widgetId={WIDGET_ID.CHANGE_CHART}
        isDownloadDisabled={isLoading || isEmpty(data) || isDownloading}
        onDownload={onDownload}
        selectedGroupBy={selectedGroupBy}
        onGroupBySelect={setSelectedGroupBy}
        onMetricSelect={(value) => setSelectedMetric(value as ChangeChartMetric)}
        selectedMetric={selectedMetric}
        onUnratedToggle={setUnratedToggleOn}
        unratedToggleOn={unratedToggleOn}
        showUnratedToggleTitle
        dataTestId="change-chart"
      />
    );
  };

  const renderContent = () => {
    if (isEmpty(data)) {
      return <NoResults />;
    }
    return (
      <div className={cx('chart-container')}>
        <BarChart ref={chartHandle} option={chartOptions} onChartInitialized={onChartInitialized} />
        {isDownloading && (
          <div className={cx('download-loader-container')}>
            <Loader position="center" />
          </div>
        )}
      </div>
    );
  };

  return (
    <Widget
      classNames={cx('body')}
      headerTitle={selectedMetricTitle}
      firstLine={renderFirstLine()}
      hideFirstLineWhenLoading={false}
      isLoading={isLoading}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      dataTestId="change-chart-widget"
      ref={widgetRef}
    >
      {renderContent()}
    </Widget>
  );
};

export default ChangeChart;
